import { InputOutputSelector } from '@modules/input-output-devices/InputOutputSelector';
import { IconButton, Tooltip, styled } from '@mui/material';
import DetectRTC from 'detectrtc';
import { Room } from 'livekit-client';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import Iconify from 'src/components/iconify';
import { useGatheringPermissions } from 'src/hooks/useGatheringPermissions';
import { useLocales } from 'src/locales';
import { TranscriptionNotesButton } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelControls/Buttons/TranscriptionNotesButton';
import { ActionIcon } from './ActionIcon';
import { HasPermissionActionIcon } from './HasPermissionActionIcon';
import { ToggleMicControl } from './ToggleMicControl';

type Props = {
  room: Room;
  isConnected: boolean;
  type: 'voice' | 'meet' | 'p2p';
  muted: boolean;
  video?: boolean;
  deafed?: boolean;
  sharing: boolean;
  chat?: boolean;
  showStopTranscription?: boolean;
  stopTranscription?: () => void;
  mute: (muted: boolean) => void;
  toggleVideo?: () => void;
  deaf?: (deafed: boolean) => void;
  startScreenShare: () => void;
  stopScreenShare: () => void;
  startCamera?: () => void;
  stopCamera?: () => void;
  toggleChat?: () => void;
  disconnect: () => void;
};

export function RTCControls({
  room,
  isConnected,
  muted,
  video,
  deafed,
  sharing,
  mute,
  deaf,
  startScreenShare,
  stopScreenShare,
  startCamera,
  stopCamera,
}: Props) {
  if (!isConnected) return null;
  const { translate } = useLocales();
  const { confirm } = useConfirmationModal();

  const {
    hasCamera,
    hasMicrophone,
    audioPermission,
    videoPermission,
    grantPermission,
  } = useGatheringPermissions();

  const askPermission = async () => {
    if (audioPermission === 'denied' || videoPermission === 'denied') {
      await confirm({
        title: translate('communicate.rtc_controls.permission_denied_title'),
        message: translate(
          'communicate.rtc_controls.permission_denied_subtitle',
        ),
        cancelButton: false,
        confirmText: 'OK',
      });
      return;
    }
    await grantPermission(DetectRTC.hasWebcam);
  };

  return (
    <CustomButtonsContainer>
      <TranscriptionNotesButton variant="iconButton" />

      <ActionIcon
        tooltip={
          sharing
            ? translate('communicate.rtc_controls.stop_screen_share')
            : translate('communicate.rtc_controls.screen_share')
        }
        onClick={() => (sharing ? stopScreenShare() : startScreenShare())}
        isActive={isConnected}
        isEnabled={sharing}
        inactiveIcon="tabler:screen-share"
        activeIcon="tabler:screen-share"
      />

      <HasPermissionActionIcon
        tooltip={
          !!video
            ? translate('communicate.rtc_controls.hide_camera')
            : translate('communicate.rtc_controls.show_camera')
        }
        onClick={() => (video ? stopCamera?.() : startCamera?.())}
        isActive={!!video}
        isEnabled={!!video}
        inactiveIcon="tabler:video-off"
        activeIcon="tabler:video"
        hasPermission={hasCamera}
        askPermission={askPermission}
      />

      <HasPermissionActionIcon
        tooltip={
          deafed
            ? translate('communicate.rtc_controls.unmute_speaker')
            : translate('communicate.rtc_controls.mute_speaker')
        }
        onClick={() => deaf?.(!deafed)}
        isActive={!deafed}
        isEnabled={false}
        inactiveIcon="tabler:headphones-off"
        activeIcon="tabler:headphones"
        hasPermission={hasMicrophone}
        askPermission={askPermission}
      />

      <ToggleMicControl
        muted={muted}
        setMuted={mute}
        isSpeaking={room?.localParticipant.isSpeaking}
      />

      <PopoverTrigger popover={InputOutputSelector}>
        <Tooltip
          placement="top"
          arrow
          title={translate('communicate.rtc_controls.device_settings')}
        >
          <span>
            <IconButton size="small" disabled={!isConnected}>
              <Iconify icon="tabler:dots-vertical" />
            </IconButton>
          </span>
        </Tooltip>
      </PopoverTrigger>
    </CustomButtonsContainer>
  );
}

const CustomButtonsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 0),
  alignItems: 'center',
  justifyContent: 'space-between',
}));
